import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '@/components/icons'
import { SmartImage } from '@/components/SmartImage'
import { Button, Card, CardBody, Empty, Input } from '@/components/ui'
import { SaveTemplateDialog, type TemplateFormValues } from '@/components/SaveTemplateDialog'
import {
  TEMPLATES,
  TEMPLATE_CATEGORIES,
  templatesOfCategory,
  type TemplateCategoryId,
  type TemplateItem,
} from '@/config/catalog'
import { customTemplateShots, customToTemplateItem, storyboardWithEditedShots, templateTitle, toAnyTemplate } from '@/config/videoTemplates'
import { useVideoTemplates } from '@/context/VideoTemplatesContext'
import { useToast } from '@/context/ToastContext'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import type { CustomVideoTemplate } from '@/api/types'

/**
 * 模板墙 —— **全站唯一实现**。
 *
 * ## 为什么抽成组件（2026-09-29）
 *
 * 用户原话：「首页改为模板中心为主，保留 [四张统计卡]，其他不需要」。
 * 于是首页的主体就是这面墙。而 `/hot-styles` 原本是另一面同样的墙 ——
 * 两份实现消费同一份 `TEMPLATES`，改一处漏一处只是时间问题
 * （这个项目的铁律：**交互控件只许一份实现**）。
 * 所以墙收进本文件，`/hot-styles` 退化为指向首页的重定向别名，
 * 顶栏「模板中心」直接落在首页的这面墙上。
 *
 * ## 2026-09-29 整批换过内容
 *
 * 用户配四张参考截图标明要什么 —— 短视频爆款 / 套图爆款 / 社媒模板 / 文案模板。
 * 旧版按我们自己的能力分 6 类 12 套（电商主图 / 商品场景图 / 展台背景 / …），
 * 封面是扁平矢量插画。两处根因：
 *  1. **分类视角错了**。按能力分（「营销素材」）是站在我们这边说话；用户进来时
 *     脑子里想的是「别人家那条会动的服装」。分类改成内容形态，与参考站对齐。
 *  2. **封面是插画**。模板墙卖的就是「出来大概长什么样」，插画给不出这个信息。
 *     封面全换成真实摄影（生成清单见 `scripts/template-assets.json`）。
 *
 * 搜索、收藏（存本机）、`?category=` 深链都保留 —— 它们与「模板内容好不好」
 * 是两件事。默认「全部」视图按分类分节：20 张卡铺成一屏网格时四类混在一起
 * 分不清哪张是哪类，分节后每节带一句这类的用途。
 */

/**
 * 分节折叠后**保留几张** —— 「一行的量」。
 *
 * ## 为什么要有这个数（2026-10-08）
 *
 * 用户口径：「整个系统如果字体都大点…」「说实话我的UI是不是太臃肿了」，随后
 * 「可以改，你一个个解决」。首页此前是**唯一一个滚 5.86 屏的页面**：23 套模板
 * 不分主次平铺，其中「我的模板」只有 1 套却也占掉一整行（1 张卡 + 右侧约 700px 空白）。
 *
 * ## 这个数字是怎么定出来的
 *
 * 真机实测（`scripts/_probe-fold.py`，1440×900）：
 *   视口 1600 → 网格 1306px → **5 列** · 卡 248×480
 *   视口 1440 → 网格 1146px → **4 列** · 卡 275×515
 *   视口 1280 → 网格  986px → **4 列** · 卡 235×462
 *   视口 1100 → 网格  806px → **3 列** · 卡 258×493
 *   视口  960 → 网格  666px → **2 列** · 卡 325×582
 *
 * 取 4 = 主流办公屏（1280 / 1440）下**正好一行**。
 *
 * ⚠️ **它必须与 `global.css` 里 `.template-grid--fold` 的 `nth-child(n + 5)` 逐数对应**
 * ——  一个管「按钮出不出现」（`items.length > FOLD_LIMIT`），一个管「藏掉第几张」。
 * 两处对不上的症状是：点了「看全部」页面纹丝不动（藏的比露的多），
 * 或者按钮不出现但有卡被藏（**点不到就永远看不见**）。
 * 它是**纯粹的布局常量**，不是业务枚举，所以没法做成单一真源
 * （`@container` / `nth-child` 都不接受 `var()`）；改这里必须同时改 CSS，
 * `npm run check` 里有一条断言把两个数字钉在一起（`smoke-fold-limit.ts`）。
 */
const FOLD_LIMIT = 4

/**
 * 案例卡的「原图 → 成品」**并排对照**（2026-10-11）。
 *
 * 取代原先封面左上角那颗「成品 / 原图」两态切换钮。换掉的理由是**客户决定**的：
 * 4-5 线制造业客户看不懂「切换」，但一眼就能看懂「左边这张随手拍 → 右边这张做出来的」；
 * 两态要点两下才凑得出对照，并排是**同时**看到，信息一步到位、不用教。
 *
 * **只共用一处实现**：模板卡（`TemplateCard`）与首页案例墙（`CaseWall`）都渲染它，
 * 差别只在外面那层容器（`.template__cover` / `.case-card__cover`，同为 3:4）。
 * 两处各写一份的结果就是「改一处漏一处」—— 本项目最忌讳的那类漂移。
 *
 * ⚠️ 只有拿到**客户真实原图**（`t.before`）时才渲染对照。没有的卡走单张成品封面，
 * **绝不借一张不相干的图冒充当原图** —— 那正是本类「封面语义必须对得上」的底线
 * （见 `catalog.ts` 里 `before` 字段的说明）。
 */
function CaseCompare({ before, cover, name }: { before: string; cover: string; name: string }) {
  return (
    <div className="case-ba">
      <div className="case-ba__half" data-side="raw">
        <SmartImage src={before} alt={`${name} 原图`} />
        <span className="case-ba__tag">原图</span>
      </div>
      <div className="case-ba__half" data-side="done">
        <SmartImage src={cover} alt={name} />
        <span className="case-ba__tag">成品</span>
      </div>
      <span className="case-ba__arrow" aria-hidden="true">
        <Icon name="chevronRight" size={14} />
      </span>
    </div>
  )
}

/**
 * 卡片上不重复渲染分类徽标 —— 「全部」视图下有分节标题，筛选后整屏同一类，徽标是纯噪音。
 *
 * `onEdit` / `onDelete` **只有自存模板才传**（见 `grid()`）：内置那二十多张是
 * 随版本发布的代码资产，页面上删不掉、也不该给出「能删」的错觉。
 * 判据就是这个：两颗键的有无完全由调用方决定，卡片自己不判断「是不是自存」——
 * 少一处 `t.custom` 分支，就少一处将来会漂的地方。
 */
function TemplateCard({
  t,
  fav,
  onFav,
  onUse,
  onEdit,
  onDelete,
  compact,
}: {
  t: TemplateItem
  fav: boolean
  onFav: () => void
  onUse: () => void
  onEdit?: () => void
  onDelete?: () => void
  /** 横向条形态（一节只有一套时用，见 `single()`）。**纯 CSS 变体**：DOM 一字不变 */
  compact?: boolean
}) {
  const custom = !!(onEdit || onDelete)
  /*
   * 「案例」类卡片把按钮从「使用模板」改成「直接做」（2026-10-11）。
   *
   * 不是因为好看：这一类的使用者是**不会写提示词**的 4-5 线制造业客户，
   * 他们看不懂「模板」这个词，但看得懂「直接做」—— 按钮文案要把「点了会发生什么」
   * 说成人话。按钮的**行为一字不变**（都是 `onUse` → 带参进工作台），
   * 只换文案，所以不产生第二份实现。
   */
  const caseBtn = t.category === 'CASE_HOT'
  /*
   * 案例卡封面：**有客户真实原图**（`t.before`）时走「原图 → 成品」并排对照
   * （见 `CaseCompare`），没有就走单张成品封面、再没有就走「征集中」占位。
   *
   * `t.cover` 也一起判：只有原图、没有成品的条目类型上可能存在 —— 那样右半幅
   * 会是一张空图，不如退回单张分支。三个条件都满足才进对照。
   */
  return (
    <div className={compact ? 'template template--row' : 'template'}>
      <div className="template__cover">
        {caseBtn && t.before && t.cover ? (
          <CaseCompare before={t.before} cover={t.cover} name={t.name} />
        ) : t.cover ? (
          <SmartImage src={t.cover} alt={t.name} />
        ) : (
          /*
           * 没有封面的案例（当前若干条都有封面，此分支是兜底）：不 P 图、不借图，
           * 直接摆一块「实拍样例征集中」的占位 —— 它同时也是对客户的一句征集。
           * 拿到真实实拍后把 `catalog.ts` 里的 `cover` 补上，占位即自动消失。
           */
          <div className="template__ph" data-kind="pending">
            <Icon name="image" size={22} />
            <span>实拍样例征集中</span>
            <span className="template__ph-sub">把产品照发来，做成这样</span>
          </div>
        )}
        {t.views ? (
          <span className="vtpl__views">
            <Icon name="play" size={10} />
            {t.views}
          </span>
        ) : null}
        <button type="button" className="template__fav" data-active={fav} onClick={onFav} aria-label="收藏">
          <svg width="14" height="14" viewBox="0 0 24 24" fill={fav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8">
            <path d="m12 3.5 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 10l6.1-.9L12 3.5Z" />
          </svg>
        </button>
      </div>
      <div className="template__body">
        {/*
          行业 / 品类 标签（2026-10-11）。
          
          只在填了 `industry` 的条目上渲染 —— 现在只有图片类（`SET_HOT`）有。
          为什么不做成「没填就占位」：本来没有这一维的分类（视频 / 社媒 / 文案）
          一整节都没有它，留一排空行反而像坏了；而填了的那一节条条都有，
          节内天然齐平。
          
          为什么放在**名字上方**而不是封面角标：封面左上角是生成图自己的中文
          标题位（见 `template-assets.json` 的写法），盖上去会压住标题；
          而右上角已经给了收藏键。
        */}
        {t.industry ? <div className="template__ind">{t.industry}</div> : null}
        <div className="template__name">{t.name}</div>
        <div className="template__desc">{t.description}</div>
        <div className="template__foot">
          <Button variant="primary" size="sm" icon="sparkles" onClick={onUse}>
            {caseBtn ? '直接做' : '使用模板'}
          </Button>
          <div className="grow" />
          {/*
            自存模板把「自动带入参数」那行字换成两颗图标键。
            不是为了好看：没有删除键的话，用户存错一条就**这辈子都清不掉**
            （模板会一直排在「我的模板」最前面）。改名键同理 —— 名字是他在一排
            卡里认人的唯一线索，打错一个字没有补救手段是说不过去的。
          */}
          {custom ? (
            <>
              {onEdit ? (
                <Button variant="ghost" size="icon" icon="edit" onClick={onEdit} title="改名字、角标与说明" aria-label="编辑模板" />
              ) : null}
              {onDelete ? (
                <Button variant="ghost" size="icon" icon="trash" onClick={onDelete} title="删除这条模板" aria-label="删除模板" />
              ) : null}
            </>
          ) : (
            <span className="text-xs muted">自动带入参数</span>
          )}
        </div>
      </div>
    </div>
  )
}

export function TemplateGallery() {
  const [sp, setSp] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [favs, setFavs] = useLocalStorage<string[]>('template_favorites_v1', [])
  // 搜索是本地过滤，不进 URL —— 这个词条是临时意图，刷新后不该残留
  const [q, setQ] = useState('')
  /**
   * 已展开的分节 id。**默认全部折叠**成一行的量。
   *
   * 与搜索词同理：这是**临时意图**（"我现在想多看两眼这类"），不进 URL、
   * 刷新即回到折叠态。进 URL 的后果是把地址栏变成一个越来越长的状态机，
   * 而同事打开你发的链接时最需要看到的是**收敛的**页面，不是你把哪几节翻开了。
   */
  const [openSecs, setOpenSecs] = useState<string[]>([])
  /** 正在改名的那条自存模板（null = 弹窗关着） */
  const [editing, setEditing] = useState<CustomVideoTemplate | null>(null)

  const {
    items: customTpls,
    error: customError,
    reload: reloadCustom,
    update: updateCustom,
    remove: removeCustom,
  } = useVideoTemplates()

  /*
   * 自存模板 → 卡片形状。**映射只在这里做一次**（见 `customToTemplateItem`）——
   * 分节、计数、筛选三处都消费同一个数组，任何一处自己 map 一遍，
   * 就会出现「计数说 3 张、页面上只有 2 张」这类不一致。
   */
  const customItems = useMemo(() => customTpls.map(customToTemplateItem), [customTpls])
  /** id → 原始记录。改名 / 删除要传的是**整条记录**（弹窗要它的 tag 与说明），不是映射后的卡片 */
  const customById = useMemo(() => new Map(customTpls.map((c) => [c.id, c])), [customTpls])

  const category = sp.get('category') ?? 'ALL'
  const valid = TEMPLATE_CATEGORIES.some((c) => c.id === category) ? category : 'ALL'

  const list = useMemo(() => {
    const byCat = templatesOfCategory(valid as TemplateCategoryId | 'ALL' | 'FAVORITES', favs, customItems)
    const kw = q.trim().toLowerCase()
    if (!kw) return byCat
    return byCat.filter(
      (t) =>
        t.name.toLowerCase().includes(kw) ||
        t.description.toLowerCase().includes(kw) ||
        (TEMPLATE_CATEGORIES.find((c) => c.id === t.category)?.name ?? '').toLowerCase().includes(kw),
    )
  }, [valid, favs, customItems, q])

  const toggleFav = (id: string) =>
    setFavs((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]))

  /**
   * 删除一条自存模板。
   *
   * 用 `window.confirm` 而不是自建弹窗 —— 本项目里凡「不可撤销的破坏性动作」
   * 都走它（见 `pages/Admin.tsx` 删账号、`pages/BatchStudio.tsx` 清表），
   * 再抽一个确认弹窗组件就等于同一件事有两份实现。
   * ⚠️ `window.confirm` 是纯文本：这里写 `**` 会原样显示星号。
   *
   * 文案里点明「已生成的作品不受影响」：模板只是参数与分镜，删它不会删片子 ——
   * 不说清的话，用户不敢删（他以为删模板会把作品一起收走）。
   */
  const removeOne = async (c: CustomVideoTemplate) => {
    if (!window.confirm(`删除模板「${templateTitle(toAnyTemplate(c))}」？\n\n只删这条模板本身，已经生成的作品不受影响。此操作不可撤销。`)) return
    try {
      await removeCustom(c.id)
      toast.success('模板已删除')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '删除失败，请稍后再试')
    }
  }

  /** 「全部」且没搜索时按分类分节 —— 四类混铺一屏分不清哪张是哪类 */
  const grouped = valid === 'ALL' && !q.trim()
  const sections = TEMPLATE_CATEGORIES.filter(
    (c) => c.id !== 'ALL' && c.id !== 'FAVORITES',
  ) as { id: TemplateCategoryId; name: string; desc: string }[]

  /**
   * 卡片接线（收藏态 / 跳转 / 改了删了）**只在这里做一次**。
   *
   * 竖卡网格与横条（`singleRow`）两种排布消费的是同一份 —— 接线抄第二遍，
   * 就会出现「横条上删不掉」「收藏了不亮」这类**只在一种排布下**的 bug，
   * 而另一处照着改的人根本看不到它。
   */
  const cardProps = (t: TemplateItem) => {
    const c = customById.get(t.id)
    return {
      t,
      fav: favs.includes(t.id),
      onFav: () => toggleFav(t.id),
      onUse: () => navigate(t.to),
      // `onEdit` / `onDelete` 只有自存模板才传（内置模板是代码资产，删不掉）
      onEdit: c ? () => setEditing(c) : undefined,
      onDelete: c ? () => void removeOne(c) : undefined,
    }
  }

  const grid = (items: TemplateItem[], fold = false) => (
    <div className={fold ? 'template-grid template-grid--fold' : 'template-grid'}>
      {items.map((t) => (
        <TemplateCard key={t.id} {...cardProps(t)} />
      ))}
    </div>
  )

  /**
   * 一节**只有一套**时用横向条，不用竖卡网格（2026-10-08）。
   *
   * 竖卡是 3:4 的大卡（1440 视口实测 275×515），单独一张后面跟着三列空白：
   * 既白占 515px 高，又让人以为「这一节的图没加载出来」——
   * 用户报的就是这个（截图里「我的模板」只有 1 套却独占一整行）。
   *
   * ⚠️ 它**不是**第二份实现：同一个 `TemplateCard`、同一组回调、
   * 同一个 `.template` 根类，只是多挂一个 `template--row` 由 CSS 改排布。
   * 所以 `.template` 的 DOM 计数不变 —— 真机校验脚本里那几条
   * 「模板卡 N 套（与真源一致）」的断言照旧成立。
   */
  const singleRow = (t: TemplateItem) => <TemplateCard key={t.id} {...cardProps(t)} compact />

  return (
    <>
      {/*
        不渲染页面级大标题 —— 顶栏已经写着「模板中心」（`ROUTE_META['/']`），
        这里再放一个同名大标题就是同一句话说两遍，还白占首屏 60px。
        只留一行「搜索 + 计数」的工具条。
      */}
      <div className="row row--wrap" style={{ gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <div style={{ width: 260 }}>
          <Input value={q} placeholder="搜索模板：短视频 / 场景 / 文案 / 社媒…" onChange={(e) => setQ(e.target.value)} />
        </div>
        {q.trim() ? (
          <span className="text-xs muted">
            匹配 {list.length} 套
            <button type="button" className="btn btn--ghost btn--sm" style={{ marginLeft: 6 }} onClick={() => setQ('')}>
              清除
            </button>
          </span>
        ) : null}
        <div className="grow" />
        {/*
          计数把它自己和预设**分开写**：自存模板是运行期数据（会随账号增减），
          混进一个总数里的话，用户看到「共 25 套」没法判断自己那几条算进去没有 ——
          而「我存的那条到底有没有存上」恰恰是他此刻最想确认的事。
        */}
        <span className="text-xs muted">
          共 {TEMPLATES.length} 套预设
          {customTpls.length ? ` · 我的模板 ${customTpls.length} 套` : ''}
          {' · '}收藏 {favs.length} 套 · 点卡片按钮（「使用模板」/「直接做」）进对应工作台并带入参数
        </span>
      </div>

      <div className="chip-row" style={{ marginBottom: 20 }}>
        {TEMPLATE_CATEGORIES.map((c) => {
          const count = templatesOfCategory(c.id, favs, customItems).length
          return (
            <button
              key={c.id}
              type="button"
              className="chip"
              data-active={valid === c.id}
              title={c.desc}
              onClick={() => setSp(c.id === 'ALL' ? {} : { category: c.id }, { replace: true })}
            >
              {c.name} {count}
            </button>
          )
        })}
      </div>

      {!list.length ? (
        <Card>
          <CardBody>
            <Empty
              icon="star"
              title={q.trim() ? '没有匹配的模板' : valid === 'FAVORITES' ? '还没有收藏的模板' : '这个分类下暂时没有模板'}
              text={
                q.trim()
                  ? '换个关键词试试，搜索会同时匹配模板名、描述和分类。'
                  : valid === 'MY_TPL'
                    ? /* 「我的模板」大概是全站唯一的**空得理所应当**的分类 —— 别让用户在这里
                         以为页面坏了，直接把他送到能造出模板的那一页去 */
                      '还没有存过模板。去视频工作台调好一套参数、出一张分镜图，再点「存为模板」—— 参数、分镜与全片约束会一起存下来，封面就是那张分镜图。'
                    : '点模板卡片右上角的星标即可收藏，收藏会存在本机。'
              }
            />
          </CardBody>
        </Card>
      ) : grouped ? (
        sections.map((s) => {
          const items = list.filter((t) => t.category === s.id)
          if (!items.length) return null

          /**
           * 只有 1 套 → 横条，**连节头按钮都不给**。
           * 「看全部 1 套」这种按钮点下去页面不会有任何变化，比没有更糟
           * （用户会以为界面坏了，然后反复点）。
           */
          const single = items.length === 1
          /** 有卡真被藏起来吗 —— 与 CSS 的 `nth-child(n + 5)` 同一个数（见 `FOLD_LIMIT`） */
          const foldable = items.length > FOLD_LIMIT
          const open = openSecs.includes(s.id)

          return (
            <section key={s.id} style={{ marginBottom: 30 }}>
              <div className="sec-head">
                <div>
                  <div className="sec-head__title" style={{ fontSize: 'var(--text-lg)' }}>
                    {s.name}
                    <span className="text-xs muted" style={{ marginLeft: 8, fontWeight: 400 }}>
                      {items.length} 套
                    </span>
                  </div>
                  <div className="sec-head__desc">{s.desc}</div>
                </div>
                {/*
                  这里原先是「只看这类」（切到该分类的独立视图）。
                  2026-10-08 换掉：分类 chip 就在上方一排，「只看这类」= 点那颗 chip，
                  同一个动作两个入口；而折叠之后用户在这一节真正想做的是
                  **「把这类铺开看」**（`?category=` 会把页面其余四节一起带走，
                  反而比原地展开更远）。所以留一颗，做原地展开。
                */}
                {foldable ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={open ? undefined : 'chevron'}
                    onClick={() =>
                      setOpenSecs((l) => (open ? l.filter((x) => x !== s.id) : [...l, s.id]))
                    }
                  >
                    {open ? '收起' : `看全部 ${items.length} 套`}
                  </Button>
                ) : null}
              </div>
              {single ? singleRow(items[0]) : grid(items, foldable && !open)}
            </section>
          )
        })
      ) : (
        grid(list)
      )}

      {/*
        编辑弹窗。与工作台那个「存为模板」**是同一个组件**（`mode="edit"`）——
        两种用法收的是同一组字段，分两个文件写，字段校验与「打开时重置」
        迟早各说各话（见组件头部注释）。

        2026-10-08 起这里也能改**模板内容**（分镜描述 / 全片约束），不再只是改名：
        用户口径「提示词模板应该是可以编辑的」—— 想动一句画面描述就得删掉重存一条
        是说不过去的。两块的初值从**这条模板已存的**那份取（这页没有「当前产品」
        这回事），且走 `customTemplateShots()` 而不是直接读 `storyboard.shots`
        （三段式模板没有那个键，理由见那个函数）。
      */}
      <SaveTemplateDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        mode="edit"
        initial={{
          name: editing?.name ?? '',
          tag: editing?.tag ?? '',
          description: editing?.description ?? '',
          shots: editing ? customTemplateShots(editing) : [],
          shot: editing?.shot ?? '',
        }}
        coverUrl={editing ? editing.coverUrl || editing.coverLocal : ''}
        onSubmit={async (v: TemplateFormValues) => {
          if (!editing) return
          await updateCustom(editing.id, {
            name: v.name,
            tag: v.tag,
            description: v.description,
            /* 分镜**整份替换 + 按原形态回写**：镜头没有稳定 id，按序号对位的话
               「删掉第二镜」会把后面每一镜都错位贴一格；而形态（三段式 / 多镜头）
               会进提示词的量词，无条件存 `{shots}` 就等于顺手把这条模板改成
               另一种形态 —— 首次套用它的人会看到「第 N 段」变「镜头 N」，
               手里刚出的那张分镜图当场作废。详见 `storyboardWithEditedShots`。 */
            storyboard: storyboardWithEditedShots(editing.storyboard, v.shots),
            shot: v.shot,
          })
          toast.success('模板已更新')
        }}
      />

      {/*
        自存清单拉失败时**只提一句**，不弹窗、不禁用页面：内置那二十多套
        （即模板中心的主体）不依赖它，为了一个附属区块把整页挡住是本末倒置。
        ⚠️ 提示里必须说明「是拉取失败、不是你真的没存过」—— 否则用户会以为
        自己存的模板全丢了。给个重试键，他不必刷新整页。
      */}
      {customError ? (
        <div className="text-xs muted" style={{ marginTop: 16 }}>
          你自己存的模板这次没拉下来（{customError}）—— 预设模板不受影响。已经存下来的不会丢。
          <button type="button" className="btn btn--ghost btn--sm" style={{ marginLeft: 6 }} onClick={() => void reloadCustom()}>
            重试
          </button>
        </div>
      ) : null}
    </>
  )
}

/**
 * 首页顶部的**横滑案例墙**（2026-10-11 新增）。
 *
 * ## 为什么要它、为什么不再复用下面那面墙
 *
 * 下面那面模板墙按分类分节、默认折叠成一行，案例虽是第一节，仍要往下滚才看得到。
 * 而「案例」是这一版说服 4-5 线客户的主力 —— 客户进来第一眼该看到的是
 * 「我这个行业能做成什么样」，而不是搜索框和四张统计卡。所以把它单独提到最上面。
 *
 * ## 它**不是**第二份模板墙
 *
 * 复用同一份 `TEMPLATES` 的 `CASE_HOT` 子集、同一批 `to` 深链、同一次 `navigate`，
 * 连封面的「原图 → 成品」对照都共用同一个 `CaseCompare`。
 * 差别只在两方面：① 排布是**横向轨道**、卡更窄；② **没有收藏 / 改名 / 删除**——
 * 那三颗是模板墙的控件，这里再挂一份只会让「收藏态」两处各存各的、互相不同步
 * （`useLocalStorage` 每个组件实例一份 state）。所以墙上只留「直接做」。
 *
 * ## ⚠️ 类名刻意避开 `.sec-head` / `.template` / 文案「看全部」
 *
 * 真机探针 `scripts/verify-templates.py` 会：数 `.sec-head__title` 的**分节数**、
 * 数 `.template` 的**卡片数**、还会**点掉所有 `.sec-head` 里含「看全部」的按钮**。
 * 案例墙若用了同名类或同名文案，会被它当成模板墙的一部分点掉，
 * 连带把首页筛到 `?category=CASE_HOT`，后面每一条断言都会跟着红。
 * 所以这里一律用 `case-*` 类、按钮文案写「全部案例」。
 */
export function CaseWall() {
  const navigate = useNavigate()
  const cases = useMemo(() => TEMPLATES.filter((t) => t.category === 'CASE_HOT'), [])
  if (!cases.length) return null
  return (
    <section className="case-wall">
      <div className="case-wall__head">
        <div>
          <div className="case-wall__title">
            案例
            <span className="text-xs muted" style={{ marginLeft: 8, fontWeight: 400 }}>
              {cases.length} 套
            </span>
          </div>
          <div className="case-wall__desc">按品类看成品样例；有真实原图的会摆出「原图 → 成品」对照 —— 看中哪张，点「直接做」，带着同款参数进对应工作台</div>
        </div>
        <Button variant="ghost" size="sm" icon="chevronRight" onClick={() => navigate('/?category=CASE_HOT')}>
          全部案例
        </Button>
      </div>
      <div className="case-rail">
        {cases.map((c) => (
          <div key={c.id} className="case-card">
            <div className="case-card__cover">
              {c.before && c.cover ? (
                /* 与模板卡**同一份**对照实现（`CaseCompare`）—— 有真实原图才出并排 */
                <CaseCompare before={c.before} cover={c.cover} name={c.name} />
              ) : c.cover ? (
                <SmartImage src={c.cover} alt={c.name} />
              ) : (
                /* 与模板墙同一块占位（`.template__ph`）：没有实拍就如实说「征集中」 */
                <div className="template__ph" data-kind="pending">
                  <Icon name="image" size={20} />
                  <span>实拍样例征集中</span>
                </div>
              )}
            </div>
            <div className="case-card__body">
              <div className="case-card__name">{c.name}</div>
              <Button variant="primary" size="sm" icon="sparkles" onClick={() => navigate(c.to)}>
                直接做
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
